import React, { useMemo } from 'react';
import { Contract, EPageViewType, Group, ENewOrUpdatePrices } from '../../types';
import { ContractDetailsTab } from './ContractDetailsTab';
import { ContractBillingTab } from './ContractBillingTab';
import { ServiceDetailsTab } from './ServiceDetailsTab';
import { MpidsTab } from './MpidsTab';
import { PremiumPricesTab } from './PremiumPricesTab';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { CustomerInvoiceTab } from '../group/CustomerInvoiceTab';
import { useContractValidationSchema } from '../../hooks/useContractValidationSchema';
import { FormButton } from '@portal_components/components/FormButton';
import { FormErrorList } from '@portal_components/components/FormErrorList';
import { FormTabHeadings } from '@portal_components/components/FormTabHeadings';

enum EContractTab {
  CONTRACT_DETAILS = 'Contract Details',
  BILLING = 'Billing',
  SERVICE_DETAILS = 'Service Details',
  // ? Featues are disabled for now
  // CONTRACT_FEATURES = 'Contract Features',
  INVOICE = 'Invoices',
  MPIDS = 'MPIDs',
  PREMIUM_PRICES = 'Premium Prices'
}

interface ContractViewProps {
  viewType: EPageViewType;
  group: Group;
  contract?: Contract;
  formSubmitted?: boolean;
  onSubmit?: (values: Contract) => Promise<{ success: boolean; message: string }>;
}

export const ContractView = ({ viewType, group, contract, formSubmitted, onSubmit }: ContractViewProps) => {
  const { isLoading: standingDataLoading } = useContractStandingData();

  const isSupplier = React.useMemo(() => {
    return group.customerGroupTypeId === '2';
  }, [group]);

  const validationSchema = useContractValidationSchema(viewType, isSupplier);

  const [submitResponse, setSubmitResponse] = React.useState<{ success: boolean; message: string } | null>(null);
  const methods = useForm<Contract>({
    values: contract
      ? {
          ...contract,
          invoices: contract.contractId ? (group.invoices ?? []).filter(invoice => invoice.contractId == contract.contractId) : [],
          updateOrNewPrices: viewType === EPageViewType.EDIT && !contract.updateOrNewPrices ? ENewOrUpdatePrices.UPDATE : undefined
        }
      : undefined,
    resolver: yupResolver<Contract>(validationSchema as any)
  });

  const [currentTab, setCurrentTab] = React.useState<EContractTab>(EContractTab.CONTRACT_DETAILS);
  const readonly = viewType === EPageViewType.VIEW || viewType === EPageViewType.RENEW;

  const handleTabSelected = (tab: EContractTab) => () => {
    setCurrentTab(tab);
  };

  const [contractStartDate, contractEndDate] = methods.watch(['contractStartDate', 'contractEndDate']);
  useMemo(() => {
    if (contractStartDate !== contract?.contractStartDate || contractEndDate !== contract?.contractEndDate) {
      methods.setValue('updateOrNewPrices', ENewOrUpdatePrices.UPDATE);
      methods.setValue('dateChange', true);
    }
    methods.setValue('dateChange', false);
  }, [contractStartDate, contract?.contractStartDate, contract?.contractEndDate, contractEndDate, methods]);

  const handleSubmit = async (values: Contract) => {
    setSubmitResponse(null);
    if (!onSubmit) return;
    const res = await onSubmit(values);
    setSubmitResponse(res);
  };

  if (standingDataLoading) return <div className="flex flex-row justify-center">Loading Standing Data...</div>;

  return (
    <div className="flex flex-col justify-start">
      <FormProvider {...methods}>
        <form onSubmit={onSubmit && !formSubmitted ? methods.handleSubmit(handleSubmit) : undefined}>
          <FormTabHeadings<EContractTab> tabs={Object.values(EContractTab)} conditions={{ [EContractTab.MPIDS]: () => isSupplier }} currentTab={currentTab} handleTabSelected={handleTabSelected} />
          {currentTab === EContractTab.CONTRACT_DETAILS && <ContractDetailsTab viewType={viewType} readonly={readonly} />}
          {currentTab === EContractTab.BILLING && <ContractBillingTab readonly={readonly} viewType={viewType} />}
          {currentTab === EContractTab.SERVICE_DETAILS && <ServiceDetailsTab readonly={readonly} viewType={viewType} />}
          {/* Contract features are disabled for now as they might not be needed */}
          {/* {currentTab === EContractTab.CONTRACT_FEATURES && <ContractFeaturesTab readonly={readonly} />} */}
          {currentTab === EContractTab.INVOICE && <CustomerInvoiceTab readonly={readonly} forContract />}
          {currentTab === EContractTab.MPIDS && isSupplier && <MpidsTab readonly={readonly} />}
          {currentTab === EContractTab.PREMIUM_PRICES && <PremiumPricesTab readonly={readonly} />}
          <FormErrorList />
          {submitResponse && <div className={`pb-2 text-sm ${submitResponse.success ? 'text-feedback-green' : 'text-feedback-red'}`}>{submitResponse.message}</div>}
          {viewType !== EPageViewType.VIEW && (
            <FormButton type="submit" disabled={formSubmitted} primary>
              Submit
            </FormButton>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
